/**
 * Incasso Kanban Types
 *
 * Frontend types matching backend entities for the incasso kanban system.
 */

// ─── Enums ────────────────────────────────────────────────────────────────────

export enum IncassoPhase {
  GEEN_CONTACT = 'GEEN_CONTACT',
  NIET_BEREIKBAAR_OF_ONDUIDELIJK = 'NIET_BEREIKBAAR_OF_ONDUIDELIJK',
  MINNELIJKE_REGELING = 'MINNELIJKE_REGELING',
  JURIDISCHE_PROCEDURE_OP_TE_STARTEN = 'JURIDISCHE_PROCEDURE_OP_TE_STARTEN',
  JURIDISCHE_PROCEDURE_LOPEND = 'JURIDISCHE_PROCEDURE_LOPEND',
  INCASSO_VIA_DERDE = 'INCASSO_VIA_DERDE',
  UITHUISZETTING = 'UITHUISZETTING',
}

export enum IncassoUrgency {
  POSITIVE = 'POSITIVE',
  UNCERTAIN = 'UNCERTAIN',
  NEGATIVE = 'NEGATIVE',
  RED_ALERT = 'RED_ALERT',
}

export enum IncassoBeoordeling {
  KOMT_IN_ORDE = 'KOMT_IN_ORDE',
  NIET_BEREIKBAAR = 'NIET_BEREIKBAAR',
  KOMT_NIET_IN_ORDE = 'KOMT_NIET_IN_ORDE',
}

// ─── Contact & Company (returned by backend leftJoinAndSelect) ───────────────

export interface IncassoContact {
  id: number;
  firstName: string;
  lastName: string;
  phone_mobile: string | null;
  phone_direct: string | null;
  email: string | null;
  notes: string | null;
}

export interface IncassoCompany {
  id: number;
  name: string;
  phone_direct: string | null;
  email: string | null;
  notes: string | null;
}

// ─── Core Entity ──────────────────────────────────────────────────────────────

export interface IncassoTicket {
  id: string | number;

  // Odoo partner link
  odooPartnerId: number;
  odooPartnerDisplayName: string;

  // Kanban phase
  phase: IncassoPhase;

  // Snooze state
  snoozeUntil: string | null;
  snoozeReason: string | null;
  snoozedAt: string | null;
  snoozedByUserId: string | null;

  // Urgency / sentiment
  urgency: IncassoUrgency | null;

  // Assessment (replaces urgency)
  beoordeling: IncassoBeoordeling | null;

  // Reason for assessment (required for KOMT_IN_ORDE / KOMT_NIET_IN_ORDE)
  beoordelingReden: string | null;

  // Notes
  notes: string | null;

  // Relations (populated by backend leftJoinAndSelect)
  contactId: number | null;
  companyId: number | null;
  contact: IncassoContact | null;
  company: IncassoCompany | null;

  // ─── Cached enrichment fields (populated during sync, fast DB-only) ─────────

  /** Total outstanding debt (cached from Odoo during sync) */
  cachedTotalDebt: number;

  /** Number of open invoices (cached) */
  cachedInvoiceCount: number;

  /** Maximum days overdue (cached) */
  cachedMaxDaysOverdue: number;

  /** Building name (cached from Odoo invoice analytic_account) */
  cachedBuildingName: string | null;

  /** Unit label (cached) */
  cachedUnitLabel: string | null;

  /** Company/vennootschap name (cached from unit → building → company) */
  cachedCompanyName: string | null;

  /**
   * Opzichmogelijkheden van de huurder: wanneer er bezichtigingen mogen doorgaan en wie
   * ze doet. Komt van het huurcontract van de unit (`visitationDays` /
   * `visitationResponsible`) en wordt bij een opzeg ingevuld — enkel meegeleverd op het
   * dossierdetail, niet in de lijst.
   */
  visitation?: {
    contractId: number;
    contractPhase: string | null;
    /** `null` = nog niet op het contract ingevuld. */
    days: string | null;
    responsible: string | null;
  } | null;

  // Legacy enriched fields (kept for backward compat with kanban)
  buildingName: string | null;
  unitLabel: string | null;

  // Audit
  createdByUserId: string;
  createdAt: string;
  updatedAt: string;

  // ─── Computed / enriched fields (returned by backend when available) ────────

  /** Total outstanding debt across all open invoices (euros) */
  totalDebt?: number;

  /** Number of open invoices for this partner */
  invoiceCount?: number;

  /** Maximum days overdue across all open invoices */
  maxDaysOverdue?: number;

  /** Total number of incasso actions logged */
  incassoActionCount?: number;

  /** Total number of legal procedures linked */
  legalProcedureCount?: number;

  /** Total number of payment plans linked */
  paymentPlanCount?: number;

  /** Number of currently active (non-closed) legal procedures */
  activeLegalProcedures?: number;

  /** Number of currently active (non-closed) payment plans */
  activePaymentPlans?: number;

  /** ISO date string of the most recent action */
  lastActionDate?: string | null;

  /** Number of days the ticket has been in the current phase */
  daysInCurrentPhase?: number;

  /** Whether the ticket is currently snoozed (snoozeUntil > now) */
  isSnoozed?: boolean;

  /** Reference and next hearing date of the active legal procedure (if any) */
  activeLegalReference?: string | null;
  activeLegalNextDate?: string | null;

  /** Active payment plan progress, e.g. "3/6" */
  paymentPlanProgress?: string | null;
}

// ─── Query Params ─────────────────────────────────────────────────────────────

/**
 * Groepeert wanbetalers in "kleine" (< €500) en "grote" (≥ €500) dossiers,
 * zodat de incassomedewerker prioriteit kan geven aan grote openstanden.
 */
export type IncassoDebtBucket = 'BELOW_500' | 'FROM_500';

/** Drempelbedrag (euro) tussen kleine en grote wanbetalers */
export const INCASSO_DEBT_BUCKET_THRESHOLD = 500;

export interface IncassoTicketQueryParams {
  phase?: IncassoPhase;
  phases?: IncassoPhase[];
  urgency?: IncassoUrgency;
  beoordeling?: IncassoBeoordeling;
  includeSnoozed?: boolean;
  search?: string;
  buildingNames?: string[];
  companyNames?: string[];
  debtBucket?: IncassoDebtBucket;
  noIncassoActions?: boolean;
  sortBy?: string;
  sortDirection?: 'ASC' | 'DESC';
  page?: number;
  pageSize?: number;
}

// ─── Snooze ───────────────────────────────────────────────────────────────────

export interface SnoozeRequest {
  snoozeUntil: string; // ISO date string
  reason: string;
}

// ─── Paginated Response ───────────────────────────────────────────────────────

export interface IncassoTicketPage {
  data: IncassoTicket[];
  total: number;
  page: number;
  pageSize: number;
  hasMore: boolean;
}

// ─── Stats ────────────────────────────────────────────────────────────────────

export interface IncassoStats {
  totalOutstanding: number;
  activeCases: number;
  snoozedCases: number;
  phaseCounts: Record<IncassoPhase, number>;
  paymentPlanAmount: number;
  legalProcedureAmount: number;
  snoozedAmount: number;
  incassoViaDerdeAmount: number;
  lastSnapshotAt: string | null;
}
